// MUI components
import { useState, useContext } from 'react'
import { IntakeContext } from '../context/IntakeContext'
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'
import { Button, Box, Grid, Typography } from '@mui/material'
import {
  format,
  subMonths,
  subYears,
  differenceInMonths,
  differenceInYears,
} from 'date-fns'

const OnboardingMilitaryReserves = ({
  id,
  preHeader,
  header,
  subHeader,
  helpText,
}) => {
  const { intakeState, updateIntakeStateAtKey } = useContext(IntakeContext)
  const [serviceTimeQuestion, setServiceTimeQuestion] = useState(true)
  const [yearsEntered, setYearsEntered] = useState(0)
  const [monthsEntered, setMonthsEntered] = useState(0)

  const dateToYears = intakeState.militaryActiveDutyStartDate
    ? differenceInYears(
        new Date(),
        new Date(intakeState.militaryActiveDutyStartDate)
      )
    : ''

  const dateToMonths = intakeState.militaryActiveDutyStartDate
    ? differenceInMonths(
        new Date(),
        new Date(intakeState.militaryActiveDutyStartDate)
      ) -
      (dateToYears > 0 ? dateToYears : yearsEntered) * 12
    : ''

  const yearsToDate = (years) => {
    return format(subYears(new Date(), years), 'yyyy-MM-dd')
  }
  const monthsToDate = (months) => {
    const totalTime =
      Number(dateToYears > 0 ? dateToYears : yearsEntered) * 12 + Number(months)
    return format(subMonths(new Date(), totalTime), 'yyyy-MM-dd')
  }

  const [reservesServiceTimeQuestion, setReservesServiceTimeQuestion] =
    useState(false)
  const [reservesYearsEntered, setReservesYearsEntered] = useState(0)
  const [reservesMonthsEntered, setReservesMonthsEntered] = useState(0)

  const reservesDateToYears = intakeState.militaryReservesStartDate
    ? differenceInYears(
        new Date(),
        new Date(intakeState.militaryReservesStartDate)
      )
    : ''

  const reservesDateToMonths = intakeState.militaryReservesStartDate
    ? differenceInMonths(
        new Date(),
        new Date(intakeState.militaryReservesStartDate)
      ) -
      (reservesDateToYears > 0 ? reservesDateToYears : reservesYearsEntered) *
        12
    : ''

  const reservesYearsToDate = (years) => {
    return format(subYears(new Date(), years), 'yyyy-MM-dd')
  }
  const reservesMonthsToDate = (months) => {
    const totalTime =
      Number(
        reservesDateToYears > 0 ? reservesDateToYears : reservesYearsEntered
      ) *
        12 +
      Number(months)
    return format(subMonths(new Date(), totalTime), 'yyyy-MM-dd')
  }

  return (
    <Box sx={{ textAlign: 'left' }}>
      <Box>
        <Typography variant="subtitle1" fontSize={{ xs: '16px', sm: '20px' }}>
          {preHeader || ''}
        </Typography>
      </Box>
      <Box mb={1}>
        <Typography variant="h4" fontSize={{ xs: '20px', sm: '24px' }}>
          {header || "Let's start with your basic information:"}
        </Typography>
      </Box>
      <Typography
        variant="body1"
        sx={{ lineHeight: '1.2' }}
        fontSize={{ xs: '16px', sm: '20px' }}
      >
        {subHeader ||
          'Let us know your name and other basic details about you!'}
      </Typography>
      <Box component="form" py={3}>
        <Grid container spacing={1}>
          {reservesServiceTimeQuestion ? (
            <>
              <Grid item xs={12}>
                <Box mt={2}>
                  <Typography variant="body1Medium">
                    Reserves Start Date
                  </Typography>
                </Box>
                {renderMuiFormField({
                  autoFocus: false,
                  id: 'militaryReservesStartDate',
                  label: <>{/* <span>Reserves Start Date</span> */}</>,
                  enableHelpText: true,
                  helpText: helpText,
                  placeholder: 'mm/dd/yyyy',
                  inputType: 'splitdate',
                  value: intakeState.militaryReservesStartDate,
                  onChange: (militaryReservesStartDate) =>
                    updateIntakeStateAtKey({ militaryReservesStartDate }),
                })}
              </Grid>
              <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-start">
                  <Button
                    variant="text"
                    color="info"
                    sx={{ height: '100%' }}
                    onClick={() => {
                      setReservesServiceTimeQuestion(false)
                      setReservesYearsEntered(0)
                      setReservesMonthsEntered(0)
                    }}
                  >
                    Enter Reserves Years and Months Served Instead
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box mt={2}>
                  <Typography variant="body1Medium">
                    Reserves Discharge Date
                  </Typography>
                </Box>
                {renderMuiFormField({
                  autoFocus: false,
                  id: 'militaryReservesEndDate',
                  label: <>{/* <span>Reserves Discharge Date</span> */}</>,
                  enableHelpText: true,
                  helpText: helpText,
                  placeholder: 'mm/dd/yyyy',
                  inputType: 'splitdate',
                  value: intakeState.militaryReservesEndDate,
                  onChange: (militaryReservesEndDate) =>
                    updateIntakeStateAtKey({ militaryReservesEndDate }),
                })}
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12}>
                <Typography variant="body1Medium">
                  Reserves Time Served
                </Typography>
              </Grid>
              <Grid item xs={6}>
                {renderMuiFormField({
                  label: (
                    <>
                      <span>Reserves Years Served</span>
                    </>
                  ),
                  enableHelpText: true,
                  helpText: helpText,
                  inputType: 'int',
                  id: 'ReservesYears',
                  placeholder: 'Ex: 3',
                  value: reservesDateToYears,
                  onChange: (e) => {
                    const newYears = Number(e)
                    setReservesYearsEntered(newYears)

                    const totalMonths =
                      newYears * 12 + Number(reservesMonthsEntered)
                    updateIntakeStateAtKey({
                      militaryReservesEndDate: format(new Date(), 'yyyy-MM-dd'),
                      militaryReservesStartDate: format(
                        subMonths(new Date(), totalMonths),
                        'yyyy-MM-dd'
                      ),
                    })
                  },
                })}
              </Grid>
              <Grid item xs={6}>
                {renderMuiFormField({
                  label: (
                    <>
                      <span>Reserves Months Served</span>
                    </>
                  ),
                  enableHelpText: true,
                  helpText: helpText,
                  inputType: 'int',
                  id: 'ReservesMonths',
                  placeholder: 'Ex: 3',
                  value: reservesDateToMonths,
                  onChange: (e) => {
                    const newMonths = Number(e)
                    setReservesMonthsEntered(newMonths)

                    const totalMonths =
                      Number(reservesYearsEntered) * 12 + newMonths
                    updateIntakeStateAtKey({
                      militaryReservesEndDate: format(new Date(), 'yyyy-MM-dd'),
                      militaryReservesStartDate: format(
                        subMonths(new Date(), totalMonths),
                        'yyyy-MM-dd'
                      ),
                    })
                  },
                })}
              </Grid>
              <Grid item xs={12} mb={1}>
                <Box display="flex" justifyContent="flex-start">
                  <Button
                    variant="text"
                    color="info"
                    sx={{ height: '100%' }}
                    onClick={() => setReservesServiceTimeQuestion(true)}
                  >
                    Enter Reserves Service Dates Instead
                  </Button>
                </Box>
              </Grid>
            </>
          )}
        </Grid>
      </Box>
    </Box>
  )
}

export default OnboardingMilitaryReserves
